// Mobile view component
import React from "react";
import {format} from "date-fns";

const BatchMobileView = ({
	                         batches,
	                         expandedBatchId,
	                         toggleExpand,
	                         renderStatusBadge,
	                         calculateBatchQuantities,
	                         renderActionButtons,
	                         handleAllocateMaterials,
	                         navigate,
	                         processingAction
                         }) => (
	<div className="space-y-4">
		{batches.map(batch => {
			const quantities = calculateBatchQuantities(batch);
			const isExpanded = expandedBatchId === batch.id;

			return (
				<div key={batch.id} className="mb-4 border rounded-lg overflow-hidden bg-white shadow">
					<div
						className={`p-4 ${isExpanded ? 'bg-blue-50' : ''}`}
						onClick={() => toggleExpand(batch.id)}
					>
						<div className="flex justify-between items-center">
							<div className="font-medium">{batch.batch_number}</div>
							<p className="mt-1 text-[10px] text-gray-600">
								{batch.items && batch.items.length > 0
								 ? batch.items.map((item) => (
										<p key={item.product.sku}>{item.product.sku}{item.variant == 'carbon' ? '-C' : '-S'} ({item.quantity})</p>
									))
								 : 'No items in batch'}
							</p>
							<button className="text-gray-500">
								<svg
									className={`h-5 w-5 transition-transform ${isExpanded ? 'transform rotate-90' : ''}`}
									xmlns="http://www.w3.org/2000/svg"
									viewBox="0 0 20 20"
									fill="currentColor"
								>
									<path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"/>
								</svg>
							</button>
						</div>
						<div className="grid grid-cols-2 gap-2 mt-2">
							<div>
								<div className="text-xs text-gray-500">Status</div>
								<div>{renderStatusBadge(batch.status)}</div>
							</div>
							<div>
								<div className="text-xs text-gray-500">Planned Completion</div>
								<div className="text-sm">{format(new Date(batch.planned_completion_date + 'T12:00:00'), 'MM/dd/yyyy')}</div>
							</div>
							<div>
								<div className="text-xs text-gray-500">Planned Qty</div>
								<div className="text-sm">{quantities.planned}</div>
							</div>
							<div>
								<div className="text-xs text-gray-500">Completed</div>
								<div className="text-sm">{quantities.completed} / {quantities.planned}</div>
								<div className="w-full bg-gray-200 h-2 mt-1 rounded-full overflow-hidden">
									<div
										className="bg-blue-600 h-full rounded-full"
										style={{
											width: `${quantities.planned > 0 ? Math.min(100, Math.floor((quantities.completed / quantities.planned) * 100)) : 0}%`
										}}
									/>
								</div>
							</div>
						</div>
						<div className="mt-4 flex justify-between">
							{batch.status === 'planned' && (
								<button
									onClick={(e) => {
										e.stopPropagation();
										handleAllocateMaterials(batch.id);
									}}
									disabled={processingAction}
									className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50"
								>
									Allocate All
								</button>
							)}
							<div className="flex space-x-3">
								<button
									onClick={(e) => {
										e.stopPropagation();
										navigate(`/production/${batch.id}`);
									}}
									className="text-blue-600 hover:text-blue-900"
								>
									<svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
										<path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
										<path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
									</svg>
								</button>
								{batch.status !== 'completed' && (
									<button
										onClick={(e) => {
											e.stopPropagation();
											navigate(`/production/${batch.id}/edit`);
										}}
										className="text-gray-600 hover:text-gray-900"
									>
										<svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
											<path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/>
										</svg>
									</button>
								)}
							</div>
						</div>
					</div>

					{isExpanded && batch.items && batch.items.length > 0 && (
						<div className="px-4 pb-4 pt-2 bg-gray-50">
							<h3 className="text-sm font-semibold mb-2">Batch Items</h3>
							<div className="space-y-3">
								{batch.items.map((item) => (
									<div key={item.id} className="border rounded p-3 bg-white">
										<div className="grid grid-cols-2 gap-2">
											<div>
												<div className="text-xs text-gray-500">SKU</div>
												<div className="text-sm font-medium">{item.product?.sku || 'N/A'}</div>
											</div>
											<div>
												<div className="text-xs text-gray-500">Status</div>
												<div>{renderStatusBadge(item.status)}</div>
											</div>
											<div>
												<div className="text-xs text-gray-500">Quantity</div>
												<div className="text-sm">{item.quantity}</div>
											</div>
											<div>
												<div className="text-xs text-gray-500">Variant</div>
												<div className="text-sm">{item.variant || 'Standard'}</div>
											</div>
										</div>
										<div className="mt-2">
											<div className="text-xs text-gray-500">Completed</div>
											<div className="text-sm">{item.completed_quantity || 0} / {item.quantity}</div>
											<div className="w-full bg-gray-200 h-2 mt-1 rounded-full overflow-hidden">
												<div
													className="bg-green-600 h-full rounded-full"
													style={{
														width: `${item.quantity > 0 ? Math.min(100, Math.floor(((item.completed_quantity || 0) / item.quantity) * 100)) : 0}%`
													}}
												/>
											</div>
										</div>
										<div className="mt-3">
											{renderActionButtons(batch, item, true)}
										</div>
									</div>
								))}
							</div>
							{batch.has_missing_parts && (
								<div className="mt-3 w-full flex flex-col">
									<div className="text-red-600 font-medium mb-1">
										Missing parts! Cannot produce this item with current inventory.
									</div>
									<div className="bg-red-50 border border-red-100 rounded-md p-2">
										<table className="min-w-full text-sm">
											<thead>
											<tr>
												<th className="text-left font-medium text-gray-700 pr-4">Part</th>
												<th className="text-right font-medium text-gray-700 pr-4">Required</th>
												<th className="text-right font-medium text-gray-700 pr-4">Available</th>
												<th className="text-right font-medium text-gray-700">Shortage</th>
											</tr>
											</thead>
											<tbody>
											{batch.missing_parts_details.map((part, partIndex) => (
												<tr key={partIndex}>
													<td className="pr-4">{part.name}</td>
													<td className="text-right pr-4">{part.required}</td>
													<td className="text-right pr-4">{part.available_total}</td>
													<td className="text-right font-medium text-red-600">{part.shortage}</td>
												</tr>
											))}
											</tbody>
										</table>
									</div>
								</div>
							)}
						</div>
					)}
				</div>
			);
		})}
	</div>
);

export default BatchMobileView;
